import { useQuery } from "react-query";
import ApiClient from "../../../common/API";
import { USE_QUERY_DEFAULT_OPTIONS } from "../../../Constants/settings";
import moment from "moment";
import { TABLE_FRESH_HOURLY_OOS, TABLE_FRESH_HOURLY_OOS_L30D, TABLE_FRESH_HOURLY_OOS_L7D, TABLE_FRESH_HOURLY_OOS_YTD } from "./constant";


export const getSelectedOption = (value, options) => {
    if (!options) return [];
    if (!value) return [];

    if (Array.isArray(value)) {
        // If "select-all" is in the values, return all options
        if (value.includes("select-all")) {
            return options.filter(option => option.value !== "select-all");
        }
        // Otherwise map values to their corresponding options
        return value
            .map(filter => options.find(option => option.value === filter))
            .filter(value => !!value);
    }

    // Single value case
    if (value === "select-all") {
        return options.filter(option => option.value !== "select-all");
    }
    return options.find(option => option.value === value);
}

export function useAvailableDateRange(tableName) {
    return useQuery(
        [tableName, 'company_id', 'branch_id'],
        () => ApiClient().get(`data/table/${tableName}/filter_values/`, {
            params: {
                columns: "tran_date",
            }
        }).then(res => {
            return res.data.tran_date
        }), {
        ...USE_QUERY_DEFAULT_OPTIONS
    }
    );
}
//REGION
export function useRegionName(tableName, companySelected) {
    return useQuery(
        [tableName, 'company_id', 'region', companySelected], // Include filters in query key
        () => {
            const params = {
                columns: 'region',
                distinct: true
            };

            if (Array.isArray(companySelected) && companySelected.length > 0) {
                params.company_id__in = companySelected.join(',');
            }

            return ApiClient().get(`data/table/${tableName}/query/`, { params }).then(res => {
                const regions = res.data.data?.map(item => item.region)
                    .filter((value, index, self) => value && self.indexOf(value) === index);
                return regions;
            });
        }, {
        ...USE_QUERY_DEFAULT_OPTIONS,
        enabled: (Array.isArray(companySelected) && companySelected.length > 0) // Run if either filter is set
    }
    );
}

//BRANCH/STORE
export function useStoreName(tableName, companySelected, regionSelected) {
    return useQuery(
        [tableName, 'company_id', companySelected, 'region', regionSelected], // Include both filters in query key
        () => {
            // Build query params
            const params = {
                columns: 'branch_name,company_id',
                limit: 1000,
                distinct: true
            };

            if (Array.isArray(companySelected) && companySelected.length > 0) {
                params.company_id__in = companySelected.join(',');
            }

            if (Array.isArray(regionSelected) && regionSelected.length > 0 && !regionSelected.includes('select-all')) {
                params.region__in = regionSelected.join(',');
            }

            return ApiClient().get(`data/table/${tableName}/query/`, { params }).then(res => {
                const branch_names = res.data.data?.map(item => item.branch_name)
                    .filter((value, index, self) => value && self.indexOf(value) === index);
                return branch_names;
            });
        }, {
        ...USE_QUERY_DEFAULT_OPTIONS,
        enabled: (Array.isArray(companySelected) && companySelected.length > 0) || (Array.isArray(regionSelected) && regionSelected.length > 0) // Run if either filter is set
    }
    );
}

//CATEGORY
export function useCategoryName(tableName, companySelected, storeSelected) { //regionSelected,
    return useQuery(
        [tableName, 'company_id', companySelected, 'branch_name', storeSelected, 'category'], //regionSelected, 'region',
        () => {
            const params = {
                columns: 'merch_category_description',
                limit: 1000,
                distinct: true
            };

            if (Array.isArray(companySelected) && companySelected.length > 0) {
                params.company_id__in = companySelected.join(',');
            }

            // if (Array.isArray(regionSelected) && regionSelected.length > 0 && !regionSelected.includes('select-all')) {
            //     params.region__in = regionSelected.join(',');
            // }

            // if (Array.isArray(storeSelected) && storeSelected.length > 0 && !storeSelected.includes('select-all')) {
            //     params.branch_name__in = storeSelected.join(',');
            // }

            return ApiClient().get(`data/table/${tableName}/query/`, { params }).then(res => {
                const merch_category_description = res.data.data?.map(item => item.merch_category_description)
                    .filter((value, index, self) => value && self.indexOf(value) === index);
                return merch_category_description;
            });
        }, {
        ...USE_QUERY_DEFAULT_OPTIONS,
        // enable always so categories are available; backend will return all when no filters provided
        enabled: true
    }
    )
}

//top50_rank_per_merch_cat
export function useRankingSKU(tableName, storeSelected, categorySelected) {
    return useQuery(
        [tableName, storeSelected, 'branch_name', categorySelected, 'merch_category_description', 'top50_rank_per_merch_cat', 'article_number'],
        () => {
            const params = {
                columns: 'top50_rank_per_merch_cat',
                limit: 1000,
                distinct: true
            };

            return ApiClient().get(`data/table/${tableName}/query/`, { params }).then(res => {
                const top50_rank_per_merch_cat = res.data.data?.map(item => item.top50_rank_per_merch_cat)
                    .filter((value, index, self) => value && self.indexOf(value) === index);
                return top50_rank_per_merch_cat;
            });
        }, {
        ...USE_QUERY_DEFAULT_OPTIONS,
        // enable always so values are available; backend will return all when no filters provided
        enabled: true
    }

    )
}


export function useFreshHourlyOOSData({ params }) {
    const {
        company_id, region, store, category, topSKU,
        days, startDate, endDate, ...restParams
    } = params;

    // Build filters
    const filters = {
        ...restParams,
        ...(startDate && endDate && {
            tran_date__gte: moment(startDate).format("YYYY-MM-DD"),
            tran_date__lte: moment(endDate).format("YYYY-MM-DD")
        }),
        ...(company_id && { company_id }),
        ...(region && region !== 'select-all' && { region }),
        ...(store && store !== 'select-all' && { branch_name: store }),
        ...(category && { merch_category_description: category }),
        ...(topSKU && { top50_rank_per_merch_cat: topSKU }),
    };

    // Select table based on days
    // let tableName;
    // switch (days) {
    //     case '7': tableName = TABLE_FRESH_HOURLY_OOS_L7D; break;
    //     case '30': tableName = TABLE_FRESH_HOURLY_OOS_L30D; break;
    //     case 'ytd': tableName = TABLE_FRESH_HOURLY_OOS_YTD; break;
    //     default: tableName = TABLE_FRESH_HOURLY_OOS; // 3 days
    // }

    return useQuery(
        [TABLE_FRESH_HOURLY_OOS, filters],
        () =>
            ApiClient()
                .get(`data/table/${TABLE_FRESH_HOURLY_OOS}/query/`, {
                    params: {
                        columns: 'tran_date,article_number,article_description,hour_start_24h',
                        group_by: 'tran_date,article_number,article_description,hour_start_24h',
                        aggregates: 'sales_sum',
                        distinct: true,
                        limit: 50000,
                        ...filters
                    }
                })
                .then(res => {
                    const rawData = res.data?.data || [];
                    const hours = [
                        '08:00', '09:00', '10:00', '11:00', '12:00',
                        '13:00', '14:00', '15:00', '16:00', '17:00',
                        '18:00', '19:00', '20:00', '21:00', '22:00'
                    ];

                    const structured = {};
                    rawData.forEach(item => {
                        const { article_number, article_description, tran_date, hour_start_24h } = item;
                        const salesValue = item.sales_sum || item['sum__sales'] || item['sales__sum'] || 0;

                        // Initialize structure
                        if (!structured[article_number]) {
                            if (days === '3') {
                                // Nested structure for 3 days
                                structured[article_number] = {
                                    article_description,
                                    article_number
                                };
                            } else {
                                // Flat structure for 7, 30, YTD
                                structured[article_number] = {
                                    article_description,
                                    article_number,
                                    start_tran_date: startDate,
                                    end_tran_date: endDate,
                                    ...hours.reduce((acc, h) => ({ ...acc, [h]: null }), {})
                                };
                            }
                        }

                        // Populate data
                        if (days === '3') {
                            if (!structured[article_number][tran_date]) {
                                structured[article_number][tran_date] = hours.reduce((acc, h) => ({ ...acc, [h]: null }), {});
                            }
                            structured[article_number][tran_date][hour_start_24h] = salesValue;
                        } else {
                            structured[article_number][hour_start_24h] =
                                (structured[article_number][hour_start_24h] || 0) + salesValue;
                        }
                    });

                    return Object.values(structured);
                }),
        {
            ...USE_QUERY_DEFAULT_OPTIONS
        }
    );
}
